import { type FC, useContext, useMemo } from "react";

import { Colors, Skeleton } from "@snapchat/snap-design-system";
import { ErrorIcon, WarningIcon } from "@snapchat/snap-design-system-icons";

import { type LensDestination, type MyLensesLensStatus } from "../../../gql-generated/types";
import { UserInfoContext } from "../../../shared/api/contexts";
import { Flexbox } from "../../../shared/components";
import { makeDestinationsText, makeDisplayStatus } from "../../state";

import { LensDestinationsText } from "./LensDestinationsText.styles";
import { CONTAINER_HEIGHT, LensStatusWithWarningContainer, WarningSection } from "./LensStatus.styles";
import { LensStatusBadge } from "./LensStatusBadge";

interface LensStatusWithWarningProps {
    status: MyLensesLensStatus;
    hasSubmissionErrors: boolean;
    destinations: readonly LensDestination[];
    showDestinationsText: boolean;
    hasDistributionRestrictions: boolean;
    loading: boolean;
}

export const LensStatusWithWarning: FC<LensStatusWithWarningProps> = ({
    status,
    destinations = [],
    showDestinationsText,
    hasSubmissionErrors,
    hasDistributionRestrictions,
    loading,
}) => {
    const userInfo = useContext(UserInfoContext);

    const lensStatusDisplayMeta = useMemo(() => makeDisplayStatus(status, destinations), [status, destinations]);
    const destinationsText = useMemo(
        () =>
            showDestinationsText
                ? makeDestinationsText(
                      destinations,
                      userInfo?.data?.getUserProfile.userId ?? "",
                      status,
                      lensStatusDisplayMeta.name,
                  )
                : "",
        [destinations, status, lensStatusDisplayMeta.name, showDestinationsText, userInfo?.data?.getUserProfile.userId],
    );

    if (loading || userInfo?.loading) {
        return (
            <Flexbox>
                <Skeleton height={CONTAINER_HEIGHT} />
            </Flexbox>
        );
    }

    return (
        <LensStatusWithWarningContainer>
            <Flexbox flexDirection="column">
                <LensStatusBadge lensStatusDisplayMeta={lensStatusDisplayMeta} />
                {destinationsText && <LensDestinationsText>{destinationsText}</LensDestinationsText>}
            </Flexbox>
            {hasSubmissionErrors && (
                <WarningSection>
                    <ErrorIcon fill={Colors.Red.V150} />
                </WarningSection>
            )}

            {hasDistributionRestrictions && (
                <WarningSection>
                    <WarningIcon fill={Colors.Orange.V200} />
                </WarningSection>
            )}
        </LensStatusWithWarningContainer>
    );
};
